import {head, includes, isNil, isNotNil, toPairs} from 'ramda';

import {
  RateMatrixCellRequestBody,
  RateMatrixCellRequestBodySchema,
  RateMatrixRequestBody,
  VehicleFinancingBalloonPayment,
  VehicleFinancingDownPayment,
  VehicleFinancingTerm,
  VehicleFinancingTemplateType,
} from '@dms/api/sales';

import {RateMatrixValue} from '../types/RateMatrixValue';
import {parseRateMatrixCellKey} from './parseRateMatrixCellKey';

interface SerializedRateMatrix {
  rateMatrix: RateMatrixRequestBody;
  defaultBalloonPayment: VehicleFinancingBalloonPayment | null;
  defaultTerm: VehicleFinancingTerm | null;
  defaultDownPayment: VehicleFinancingDownPayment | null;
}

interface CurrentDefaultSelection {
  term: VehicleFinancingTerm | null;
  downPayment: VehicleFinancingDownPayment | null;
}

export function serializeRateMatrix(
  value: RateMatrixValue,
  type: VehicleFinancingTemplateType,
  currentDefault: CurrentDefaultSelection = {term: null, downPayment: null}
): SerializedRateMatrix {
  const cells: RateMatrixCellRequestBody[] = toPairs(value.cells)
    .map(([key, data]) => ({coordinates: parseRateMatrixCellKey(key), data}))
    .filter(({coordinates}) =>
      type === 'BALLOON' ? isNotNil(coordinates.balloonBracket) : isNil(coordinates.balloonBracket)
    )
    // Rates of terms the user has disabled must not be persisted, otherwise they'd be stored as
    // orphans (not part of `availableTerms`) and silently resurface when the term is re-enabled.
    .filter(({coordinates}) => includes(coordinates.term, value.availableTerms))
    .map(({coordinates, data}) =>
      RateMatrixCellRequestBodySchema.parse({
        term: coordinates.term,
        downPayment: coordinates.downPayment,
        balloonPayment: coordinates.balloonBracket,
        nominalRate: data.nominalRate,
        apr: data.apr,
      })
    );

  const defaultBalloonPayment = value.defaultBalloonPayment;

  const cellsEligibleAsDefault = cells.filter(
    (cell) => type === 'STANDARD' || cell.balloonPayment === defaultBalloonPayment
  );
  const preservedDefault = cellsEligibleAsDefault.find(
    (cell) => cell.term === currentDefault.term && cell.downPayment === currentDefault.downPayment
  );
  // The backend requires every non-empty matrix to reference one configured default cell. For a
  // new template the first eligible configured cell is deterministic; on edit keep the saved
  // choice while that cell still exists.
  const defaultCell = preservedDefault ?? head(cellsEligibleAsDefault) ?? null;

  return {
    rateMatrix: {availableTerms: value.availableTerms, cells},
    defaultBalloonPayment,
    defaultTerm: defaultCell?.term ?? null,
    defaultDownPayment: defaultCell?.downPayment ?? null,
  };
}
